// src/modules/units/tyres/shared/tyre-status.js
//
// Single source of truth for the tyre lifecycle status.
//
// TYRE_STATUS now holds only the ACTUAL STATES a tyre can be in: where it is or
// what it has become. No transient intermediate states.
//
// FITTED and IN_SERVICE are DERIVED from the association (sensor + unit + wheel),
// never stored — the association IS the fact. A stored "Fitted" can drift out of
// sync with the sensor it claims to be paired to; deriving keeps the two truthful.
// The sensor/unit split alone is not enough for IN_SERVICE: a currentWheelId of
// 1180 means the sensor still streams through the unit's gateway while the tyre is
// off the wheel, so it reads FITTED (paired for data), not IN_SERVICE.
//
// IN_STOCK, FITTED, IN_SERVICE are association-derived. AT_RETREAD, SCRAPPED,
// SOLD, MISSING, WRITTEN_OFF are record-owned (deliberate placements or terminal
// states).
//
// Repair and Inspection are ACTIONS, not statuses — they are logged to the action
// timeline and the tyre stays wherever it is (In Stock / Fitted / In Service). A
// tyre is never "Under Repair" or "Under Inspection". This unambiguous split
// between state and event is the whole point: the tyre's status answers "where is
// it", and the action log answers "what did we do".
//
// Every tile, filter, badge and dropdown derives from here, so the vocabulary can
// never drift between screens.

// The 1180 sentinel is a wheel-id encoding fact, so it lives in tyre-format.js;
// we import the predicate rather than re-hardcoding the number, keeping a single
// source of truth for "the sensor streams through the unit but the tyre is off
// the wheel".
import { isUnpairedWheelId } from './tyre-format.js';

export const TYRE_STATUS = Object.freeze({
  IN_STOCK: 'IN_STOCK',
  FITTED: 'FITTED', // on a sensor, not yet on a vehicle
  IN_SERVICE: 'IN_SERVICE',
  AT_RETREAD: 'AT_RETREAD',
  SCRAPPED: 'SCRAPPED',
  SOLD: 'SOLD',
  MISSING: 'MISSING',
  WRITTEN_OFF: 'WRITTEN_OFF' // came back from retread unusable — terminal, like Scrapped
});

// Ordered for display (stock -> fitted -> service -> away -> end of life).
// `tone` maps to a colour token defined in tyres.css (.tm-tone-*).
export const STATUS_META = Object.freeze({
  IN_STOCK: { label: 'In Stock', tone: 'stock' },
  FITTED: { label: 'Fitted', tone: 'fitted' }, // #185785 — primary blue
  IN_SERVICE: { label: 'In Service', tone: 'fit' }, // #aece25 — accent green
  AT_RETREAD: { label: 'At Retread', tone: 'retread' },
  SCRAPPED: { label: 'Scrapped', tone: 'slate' },
  SOLD: { label: 'Sold', tone: 'slate' },
  MISSING: { label: 'Missing', tone: 'red' },
  //WRITTEN_OFF: { label: 'Written Off', tone: 'slate' } // terminal; slate like Scrapped/Sold
});

// Every status we can DISPLAY — includes FITTED. Use for badges, legends, and any
// breakdown computed in the browser from records we already hold.
export const DISPLAY_STATUSES = Object.freeze(Object.keys(STATUS_META));

// Every status the SERVER can FILTER on. This briefly excluded FITTED, which the
// backend had not heard of; it now has, so the two lists are the same list again.
//
// Kept as its own name rather than collapsed at every call site: the distinction
// between "what we can draw" and "what we can ask the API for" is a real one, and
// the day it reopens the filter goes back here instead of into every dropdown.
export const QUERYABLE_STATUSES = DISPLAY_STATUSES;

const isBlank = (v) => v === null || v === undefined || v === '';

// ---- association predicates ----------------------------------------------
// The association fields are the authority on where a tyre physically is. These two
// replace the hand-rolled `currentSensorId == null` checks that had grown up
// independently in six different files.

/** Paired to a sensor? Fit / Move / Remove and the rotation strip all hinge on this. */
export function hasSensor(tyre) {
  return !isBlank(tyre?.currentSensorId);
}

/** On a vehicle? A wheel position without a unit is not a real fitment. */
export function hasUnit(tyre) {
  return !isBlank(tyre?.unitId);
}

/**
 * On a vehicle AND actually bolted to a wheel — the line between IN_SERVICE and
 * FITTED. A unit association alone is not enough: currentWheelId === 1180 is the
 * backend's marker for a tyre whose sensor still streams through the unit's
 * gateway after it came off the wheel. Such a tyre keeps its unit association but
 * is not in service, so "mounted" is hasUnit minus that sentinel. Isolated here so
 * effectiveStatus() reads as one clause and any future caller shares the rule.
 */
export function isMountedToWheel(tyre) {
  return hasUnit(tyre) && !isUnpairedWheelId(tyre?.currentWheelId);
}

// ---------------------------------------------------------------------------
// Bins — three derivation states (association) plus two deliberate placements.
//
// The status enum stays fine-grained: a register badge should still read "At
// Retread", not "Away". Bins are the aggregation over it, and they exist because
// the dashboard answers one question — "can I fit this?" — which eight enum
// values answer only once you have memorised which of them mean yes.
//
// Away is the one non-terminal status that is not association-derived: a tyre
// physically elsewhere (at the retreader), but still on the books and returnable
// to stock. In Stock and Fitted and In Service all derive from the association;
// Away and end-of-life (Scrapped/Sold) are deliberate placements.
// ---------------------------------------------------------------------------
export const TYRE_BIN = Object.freeze({
  IN_STOCK: 'IN_STOCK',      // on the shelf, ready to fit
  FITTED: 'FITTED',          // on a sensor, not yet on a vehicle
  IN_SERVICE: 'IN_SERVICE',  // on a sensor and on a vehicle
  AWAY: 'AWAY',              // ours, on the books, not at the depot
  MISSING: 'MISSING'         // unaccounted for
});

export const BIN_META = Object.freeze({
  [TYRE_BIN.IN_STOCK]: { label: 'In Stock', tone: 'stock' },
  [TYRE_BIN.FITTED]: { label: 'Fitted', tone: 'fitted' },
  [TYRE_BIN.IN_SERVICE]: { label: 'In Service', tone: 'fit' },
  [TYRE_BIN.AWAY]: { label: 'Away', tone: 'retread' },
  [TYRE_BIN.MISSING]: { label: 'Missing', tone: 'red' }
});

export const BIN_ORDER = Object.freeze([
  TYRE_BIN.IN_STOCK, TYRE_BIN.FITTED, TYRE_BIN.IN_SERVICE, TYRE_BIN.AWAY, TYRE_BIN.MISSING
]);

export function binLabel(bin) {
  return BIN_META[bin]?.label || bin || '';
}

// Physically elsewhere. These are the only statuses allowed to mean a location
// Away: the one non-terminal status that means a tyre is not at the depot.
const AWAY_STATUSES = new Set([
  TYRE_STATUS.AT_RETREAD
]);

// Gone: out of the fleet, and therefore out of every bin rather than quietly
// inflating In Stock.
const OUT_OF_FLEET = new Set([
  TYRE_STATUS.SCRAPPED,
  TYRE_STATUS.SOLD,
  TYRE_STATUS.WRITTEN_OFF
]);

/**
 * The status to SHOW for a tyre, in one place, in strict order of authority.
 *
 *   1. gone or unlocatable   -> the stored value; nothing can contradict it
 *   2. has a sensor          -> IN_SERVICE if it is also mounted to a wheel on a
 *                               vehicle, else FITTED. "Mounted" excludes the 1180
 *                               sentinel: a sensor still streaming through a unit's
 *                               gateway after the tyre came off the wheel is
 *                               Fitted (paired for data), not In Service.
 *   3. away stamp, no sensor -> the stored value; the tyre is off somewhere
 *   4. anything else         -> IN_STOCK
 *
 * The ordering is the design. The association is a physical fact and outranks any
 * stamp: a tyre bolted to a wheel is not at the retreader, whatever a stale status
 * says. Only the things that cannot be derived from a field at all — sold,
 * scrapped, written off, missing — are allowed to win outright.
 *
 * Note what rule 4 swallows: UNDER_INSPECTION. An inspection is an event, not a
 * Repair and Inspection: both are actions (logged to the timeline), not statuses.
 * A tyre sent for repair stays In Stock / Fitted / In Service — it does not land
 * in a "Under Repair" bin and never leave, waiting for someone to move it back out.
 * The action log says it was repaired; the status says where it is.
 */
export function effectiveStatus(tyre) {
  const stored = tyre?.tyreStatus;
  if (OUT_OF_FLEET.has(stored) || stored === TYRE_STATUS.MISSING) return stored;
  if (hasSensor(tyre)) return isMountedToWheel(tyre) ? TYRE_STATUS.IN_SERVICE : TYRE_STATUS.FITTED;
  if (AWAY_STATUSES.has(stored)) return stored;
  return TYRE_STATUS.IN_STOCK;
}

/**
 * Which bin a tyre counts in, or null for one that has left the fleet.
 * Always derived through effectiveStatus(), never read off the record, so a
 * count and a badge can never disagree about the same tyre.
 */
export function statusBin(tyre) {
  const s = effectiveStatus(tyre);
  if (OUT_OF_FLEET.has(s)) return null;
  if (AWAY_STATUSES.has(s)) return TYRE_BIN.AWAY;
  return s;
}

// End of life: the tyre has left the fleet for good. OUT_OF_FLEET plus MISSING —
// exported through isRetired() so no call site has to re-list which four count
// as gone.
const END_OF_LIFE = new Set([
  TYRE_STATUS.SCRAPPED,
  TYRE_STATUS.SOLD,
  TYRE_STATUS.WRITTEN_OFF,
  TYRE_STATUS.MISSING
]);

// Archiving is a narrower business rule than retirement: only these three
// explicit retirement outcomes qualify. WRITTEN_OFF remains retired for fleet
// reporting, but is NOT eligible for archiving. (The backend calls archiving a
// soft delete — see archiveTyre() in tyres.api.js.)
const ARCHIVE_ELIGIBLE_STATUSES = new Set([
  TYRE_STATUS.SCRAPPED,
  TYRE_STATUS.SOLD,
  TYRE_STATUS.MISSING
]);

export function isArchiveEligibleTyre(tyre) {
  return ARCHIVE_ELIGIBLE_STATUSES.has(tyre?.tyreStatus);
}

/** Already left the fleet? Guards against offering to retire a tyre twice. */
export function isRetired(tyre) {
  return END_OF_LIFE.has(effectiveStatus(tyre));
}

/** True when we know enough to show a status badge at all (guards an empty seed). */
export function hasKnownStatus(tyre) {
  return !isBlank(tyre?.tyreStatus) || hasSensor(tyre);
}

export function statusLabel(status) {
  return STATUS_META[status]?.label || status || 'Unknown';
}

export function statusTone(status) {
  return STATUS_META[status]?.tone || 'slate';
}

// ---- register status filter ----------------------------------------------
// The register's status dropdown is a FILTER vocabulary — a superset of the badge
// vocabulary. Alongside every displayable status it offers two refinements of "In
// Stock" that split the shelf by casing life: never-retreaded ("New") and retreaded.
// "How many fresh casings are on the shelf, and how many are running on a retread?"
// is a real stock question the bare In-Stock filter cannot answer.
//
// These are FILTER-ONLY. A tyre's effectiveStatus() and its badge stay "In Stock";
// only the register's dropdown knows the split — so nothing here touches STATUS_META
// or DISPLAY_STATUSES (which drive badges, and would otherwise start painting an
// impossible "In Stock - New" badge). Kept beside effectiveStatus so the filter
// vocabulary can never drift from the status vocabulary it extends.
//
// The pseudo-values are namespaced with '::' so they can never be mistaken for a
// real TYRE_STATUS the server might return in tyreStatus.
export const STATUS_FILTER = Object.freeze({
  IN_STOCK_NEW: 'IN_STOCK::NEW',
  IN_STOCK_RETREADED: 'IN_STOCK::RETREADED'
});

// Has the casing been retreaded at least once? retreadCount rides on the list rows
// (it also feeds the register's retread columns), so this is answerable client-side
// with no per-tyre fetch. A missing/blank count reads as a fresh casing (0).
function hasBeenRetreaded(tyre) {
  return (Number(tyre?.retreadCount) || 0) > 0;
}

// The ordered options for the register's status <select>, each { value, label }.
// It is DISPLAY_STATUSES with the two In-Stock refinements spliced in directly after
// In Stock, so the split reads as a sub-grouping of the shelf. Labels are built from
// the same STATUS_META label, so a future rename of "In Stock" still flows through.
export const STATUS_FILTER_OPTIONS = Object.freeze(
  DISPLAY_STATUSES.flatMap((s) => {
    const base = { value: s, label: statusLabel(s) };
    if (s !== TYRE_STATUS.IN_STOCK) return [base];
    return [
      base,
      { value: STATUS_FILTER.IN_STOCK_NEW, label: `${base.label} - New` },
      { value: STATUS_FILTER.IN_STOCK_RETREADED, label: `${base.label} - Retreaded` }
    ];
  })
);

// Does a tyre match the register's status filter? A blank value matches everything
// (the "All statuses" option). The two pseudo-values are In Stock narrowed by casing
// life; every other value is a plain DERIVED-status match, so the filter still agrees
// with the Status column and the dashboard tiles. One predicate, shared by the live
// sweep filter and the CSV export, so those two can never diverge.
export function matchesStatusFilter(tyre, value) {
  if (!value) return true;
  if (value === STATUS_FILTER.IN_STOCK_NEW) {
    return effectiveStatus(tyre) === TYRE_STATUS.IN_STOCK && !hasBeenRetreaded(tyre);
  }
  if (value === STATUS_FILTER.IN_STOCK_RETREADED) {
    return effectiveStatus(tyre) === TYRE_STATUS.IN_STOCK && hasBeenRetreaded(tyre);
  }
  return effectiveStatus(tyre) === value;
}

// Badge helpers that take a whole tyre — label and tone from the DERIVED status.
// Prefer these at call sites: they cannot accidentally read the raw stored value.
export function tyreStatusLabel(tyre) {
  return statusLabel(effectiveStatus(tyre));
}

export function tyreStatusTone(tyre) {
  return statusTone(effectiveStatus(tyre));
}
